import { useEffect } from 'react';

/*
 * This hook calculates and sets a custom CSS variable (--cvh) to represent the viewport height.
 * It addresses issues with mobile browsers where the visible viewport height can change
 * due to dynamic UI elements like the address bar. For more details, see:
 * https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
 *
 * - **Offline → Online WebView case**:
 *   - When WebView loads offline, it often initializes with an incorrect `innerHeight`
 *     because the browser UI isn’t fully rendered yet.
 *   - When internet is restored, viewport values do not update automatically because
 *     Safari/WebView doesn’t fire `resize` events for network changes.
 *   - Reference: https://stackoverflow.com/questions/50690191/window-innerheight-returning-wrong-value-in-ios-11
 *
 */
const useCustomViewportHeight = () => {
  useEffect(() => {
    const updateCustomVH = () => {
      const vh = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      document.documentElement.style.setProperty('--cvh', vh ? `${vh / 100}px` : '1dvh');
    };

    updateCustomVH();

    const attachListeners = () => {
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', updateCustomVH);
        window.visualViewport.addEventListener('scroll', updateCustomVH);
      }
      window.addEventListener('resize', updateCustomVH);
      window.addEventListener('orientationchange', updateCustomVH);
      window.addEventListener('online', updateCustomVH);
      window.addEventListener('focus', updateCustomVH);
    };

    const detachListeners = () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateCustomVH);
        window.visualViewport.removeEventListener('scroll', updateCustomVH);
      }
      window.removeEventListener('resize', updateCustomVH);
      window.removeEventListener('orientationchange', updateCustomVH);
      window.removeEventListener('online', updateCustomVH);
      window.removeEventListener('focus', updateCustomVH);
    };

    attachListeners();
    return detachListeners;
  }, []);
};

export default useCustomViewportHeight;
